import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import AnalyticsOutlinedIcon from '@mui/icons-material/AnalyticsOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import WebOutlinedIcon from '@mui/icons-material/WebOutlined';

import { formatFeatureLabel } from './rbacMappers';

const OBJECT_ICON_MAP = {
  organisation: BusinessOutlinedIcon,
  profile: SecurityOutlinedIcon,
  proposalinfo: CampaignOutlinedIcon,
  accountinfo: AccountBalanceOutlinedIcon,
  baseuser: PersonOutlinedIcon,
};

const FEATURE_ICON_MAP = {
  can_view: VisibilityOutlinedIcon,
  can_create: PersonAddOutlinedIcon,
  lead: PersonAddOutlinedIcon,
  web_form: WebOutlinedIcon,
  import: FolderOutlinedIcon,
  download: FolderOutlinedIcon,
  map: TuneOutlinedIcon,
  call: ChatOutlinedIcon,
  comment: ChatOutlinedIcon,
  disposition: TuneOutlinedIcon,
  assign: PersonOutlinedIcon,
  campaignleads: CampaignOutlinedIcon,
  delete: TuneOutlinedIcon,
  report: AnalyticsOutlinedIcon,
  analytics: AnalyticsOutlinedIcon,
  template: FolderOutlinedIcon,
  flow: TuneOutlinedIcon,
  ai: AnalyticsOutlinedIcon,
  vector: FolderOutlinedIcon,
  instruction: FolderOutlinedIcon,
  self_service: WebOutlinedIcon,
  concept: FolderOutlinedIcon,
  benchmark: AnalyticsOutlinedIcon,
  setting: SettingsOutlinedIcon,
  management: SettingsOutlinedIcon,
  intervene: ChatOutlinedIcon,
  report: AnalyticsOutlinedIcon,
  reporting: AnalyticsOutlinedIcon,
  ecommerce: AccountBalanceOutlinedIcon,
  sms: ChatOutlinedIcon,
  b2b: WebOutlinedIcon,
  user_list: PersonOutlinedIcon,
  onboard: BusinessOutlinedIcon,
  onboarding: FolderOutlinedIcon,
  campaign: CampaignOutlinedIcon,
  scheduled: ScheduleOutlinedIcon,
  settings: SettingsOutlinedIcon,
};

export function getPermissionRowIcon(row) {
  if (row.type === 'object') {
    const modelKey = (row.model || '').toLowerCase();
    return OBJECT_ICON_MAP[modelKey] || FolderOutlinedIcon;
  }

  const codename = (row.codename || '').toLowerCase();
  const matchedKey = Object.keys(FEATURE_ICON_MAP).find((key) => codename.includes(key));
  return FEATURE_ICON_MAP[matchedKey] || TuneOutlinedIcon;
}

function appendLeafRows(node, rows, groupLabel) {
  (node.features || []).forEach((feature) => {
    rows.push({
      id: `feature:${feature.codename}`,
      type: 'feature',
      label: feature.name || formatFeatureLabel(feature.codename),
      codename: feature.codename,
      groupLabel,
    });
  });

  (node.objects || []).forEach((objectDef) => {
    rows.push({
      id: `object:${(objectDef.model || '').toLowerCase()}:${groupLabel}`,
      type: 'object',
      label: objectDef.label || objectDef.model,
      model: objectDef.model,
      groupLabel,
    });
  });
}

function walkModule(node, rows, groupParts = []) {
  const nextParts = node.label ? [...groupParts, node.label] : groupParts;
  const hasChildren = Boolean(node.children?.length);
  const hasLeafContent = Boolean(node.features?.length || node.objects?.length);

  if (hasLeafContent) {
    appendLeafRows(node, rows, nextParts.slice(1).join(' › ') || undefined);
  }

  if (hasChildren) {
    node.children.forEach((child) => walkModule(child, rows, nextParts));
  }
}

export function flattenRootModuleRows(rootModule) {
  const rows = [];

  if (rootModule.features?.length || rootModule.objects?.length) {
    appendLeafRows(rootModule, rows);
  }

  (rootModule.children || []).forEach((child) => walkModule(child, rows, [rootModule.label]));

  return rows;
}

export function buildPermissionMatrixSections(catalog) {
  return (catalog?.modules || [])
    .map((module) => ({
      codename: module.codename,
      label: module.label,
      description: module.description,
      rows: flattenRootModuleRows(module),
    }))
    .filter((section) => section.rows.length > 0);
}
